import {enqueueSubmissionWithPhotos, getSubmission, listPhotoBlobsByCsid, MAX_RETRIES} from './offlineQueue.js';
import {buildRecord, getFormConfig} from './offlineForms.js';
import type {FormPayload} from './offlineForms.js';
import {newClientSubmissionId} from './clientSubmissionId.js';
import {preparePhotos} from './dailyPhotos.js';
import {queuedReportPhotoMetadata} from './queuedReportPhotos.js';
import {queuedReportActor} from './queuedReportApi.js';
import type {SupabaseClient} from './supabaseTypes.js';

export type QueuedReportRecovery = {
  csid: string;
  formKind: string;
  actorProfileId: string;
  legacyIdentity: boolean;
  payload: Record<string, unknown>;
  record: Record<string, unknown>;
  photos: File[];
};

function object(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

// Read-only: Open and Cancel do not remove, rebind or rewrite queued data.
// A previously queued replacement is the work to resume, never another copy.
export async function loadQueuedReportRecovery(
  sb: SupabaseClient,
  csid: string,
  expectedFormKind?: string,
): Promise<QueuedReportRecovery> {
  const actorProfileId = await queuedReportActor(sb);
  if (!actorProfileId) throw new Error('Sign in to review this queued report.');
  const seen = new Set<string>();
  let row = await getSubmission(csid);
  while (row?.replacement_csid) {
    if (seen.has(row.csid) || seen.size >= 64)
      throw new Error('Recovery links need help. The queued reports were retained.');
    seen.add(row.csid);
    row = await getSubmission(row.replacement_csid);
  }
  if (!row) throw new Error('This queued report is no longer on this device.');
  getFormConfig(row.form_kind);
  if (expectedFormKind && row.form_kind !== expectedFormKind) throw new Error('Open this report in its original form.');
  if (row.actor_profile_id && row.actor_profile_id !== actorProfileId) {
    throw new Error('Sign in with the account that queued this report.');
  }
  if (!object(row.record) || !object(row.payload))
    throw new Error('This older report needs manual recovery; its stored data was retained.');
  const metadata = Array.isArray(row.record.photos) ? row.record.photos : [];
  const blobs = await listPhotoBlobsByCsid(row.csid);
  if (metadata.length !== blobs.length)
    throw new Error('Some queued photo data is unavailable. The report was retained.');
  const seenPaths = new Set<string>();
  const photos = metadata.map((meta) => {
    if (!object(meta) || typeof meta.path !== 'string')
      throw new Error('Queued photo details need help. The report was retained.');
    const stored = blobs.find((photo) => photo.key === meta.path);
    if (!stored || seenPaths.has(meta.path)) throw new Error('A queued photo is unavailable. The report was retained.');
    seenPaths.add(meta.path);
    return new File([stored.blob], stored.name || 'report-photo.jpg', {type: stored.mime});
  });
  return {
    csid: row.csid,
    formKind: row.form_kind,
    actorProfileId,
    legacyIdentity: !row.actor_profile_id,
    payload: {...row.payload, photos},
    record: row.record,
    photos,
  };
}

export function recoveryText(value: unknown): string {
  return value === null || value === undefined ? '' : String(value);
}

// H1c integration seam: await this BEFORE navigating from an online duplicate
// notice. It only writes the existing local queue; no upload/create occurs.
// If preservation fails, the caller must stay on the mounted form.
export async function preserveBlockedReportDraft(
  sb: SupabaseClient,
  formKind: string,
  payload: FormPayload,
): Promise<string> {
  const actorProfileId = await queuedReportActor(sb);
  if (!actorProfileId) throw new Error('Sign in before leaving this report; its answers are still on the form.');
  const cfg = getFormConfig(formKind);
  const csid = newClientSubmissionId();
  const id = `${formKind}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const prepared =
    cfg.hasPhotos && Array.isArray(payload.photos) && payload.photos.length
      ? await preparePhotos(formKind, csid, payload.photos)
      : [];
  const sanitized = {...payload, photos: await queuedReportPhotoMetadata(prepared)};
  await enqueueSubmissionWithPhotos({
    formKind,
    csid,
    actorProfileId,
    payload: sanitized,
    record: buildRecord(formKind, sanitized, {id, csid}),
    photos: prepared,
    status: 'failed',
    retryCount: MAX_RETRIES,
    lastError: 'Needs Help: another report already exists. Open these saved answers to review.',
  });
  return csid;
}
